import React from 'react';
import { useInView } from 'react-cool-inview';
import { isMobile } from 'react-device-detect';
import classnames from 'classnames';

import type { SliderType } from '@components/ui';
import {
  Button, Icon, Link, Slide, Slider
} from '@components/ui';
import { isArrayEmpty } from '@helpers/ArrayHelpers';
import { useAnalytics } from '@hooks/useAnalytics';

import RecipeCard from '../RecipeCard/RecipeCard';

import type { IRecipeCard } from 'types';

import styles from './RecipeCarousel.module.scss';

interface BreakpointParams {
  slidesPerView?: number,
  slidesPerGroup?: number,
  spaceBetween?: number
}

interface Props {
  className?: string,
  wrapperClassName?: string,
  sliderClassName?: string,
  headerClassName?: string,
  titleClassName?: string,
  carouselClassName?: string,
  navigationButtonClassName?: string,
  //
  navigation?: boolean,
  breakpoints?: Record<number, BreakpointParams>,
  //
  name?: string,
  title?: string,
  link?: {
    text: string,
    url: string
  },
  recipes: IRecipeCard[],
  //
  onRecipeEnter?: (recipe: IRecipeCard) => void,
  onRecipeClick?: (recipe: IRecipeCard, e: React.MouseEvent<HTMLAnchorElement>) => void,
  onRecipeViewIngredientsClick?: (recipe: IRecipeCard, e: React.MouseEvent<HTMLButtonElement>) => void
}

const RecipeCarousel = (props: Props) => {

  const {
    className,
    wrapperClassName,
    sliderClassName,
    headerClassName,
    titleClassName,
    carouselClassName,
    navigationButtonClassName,
    //
    navigation = true,
    breakpoints = DEFAULT_BREAKPOINTS,
    //
    name,
    title,
    link,
    recipes,
    //
    onRecipeEnter,
    onRecipeClick,
    onRecipeViewIngredientsClick,
  } = props;

  // Refs

  const sliderRef = React.useRef<SliderType>(null);

  // Hooks

  const analytics = useAnalytics();

  const [shouldShowNavigation, setShouldShowNavigation] = React.useState(false);

  const { observe } = useInView({
    threshold: 0.75,
    onEnter: ({ unobserve }) => {
      analytics.viewRecipeCarousel(name || title);
      unobserve();
    }
  });

  // Effects

  React.useEffect(() => {
    setShouldShowNavigation(navigation);
  }, [navigation]);

  // Handlers

  const handleRecipeEnter = (recipe: IRecipeCard) => {
    analytics.viewRecipeCard(recipe?.id);

    if (onRecipeEnter) {
      onRecipeEnter(recipe);
    }
  };

  const handleRecipeClick = (recipe: IRecipeCard, e: React.MouseEvent<HTMLAnchorElement>) => {
    analytics.selectRecipeCard(recipe?.id);

    if (onRecipeClick) {
      onRecipeClick(recipe, e);
    }
  };

  const handleRecipeViewIngredientsClick = (recipe: IRecipeCard, e: React.MouseEvent<HTMLButtonElement>) => {
    analytics.selectRecipeViewIngredients(recipe?.id);

    if (onRecipeViewIngredientsClick) {
      onRecipeViewIngredientsClick(recipe, e);
    }
  };

  // Empty

  if (isArrayEmpty(recipes)) return null;

  // Render

  return (
    <div
      ref={observe}
      className={classnames(styles.root, className)}
    >

      {/* Header */}
      {
        (title || link) && (
          <div className={classnames(styles.header, headerClassName)}>

            {/* Title */}
            {
              title && (
                <div className={classnames(styles.title, titleClassName)}>
                  {title}
                </div>
              )
            }

            {/* Link */}
            {
              link && (
                <ViewMoreLink
                  className={styles.link}
                  name={title}
                  link={link}
                />
              )
            }
          </div>
        )
      }

      {/* Carousel */}
      <Slider
        className={classnames(styles.carousel, carouselClassName)}
        wrapperClassName={classnames(styles.carouselWrapper, wrapperClassName)}
        sliderClassName={classnames(styles.carouselSlider, sliderClassName)}
        prevClassName={classnames(styles.carouselPrev, navigationButtonClassName)}
        nextClassName={classnames(styles.carouselNext, navigationButtonClassName)}
        //
        spaceBetween={24}
        slidesPerView={1}
        slidesPerGroup={1}
        breakpoints={breakpoints}
        //
        navigation={shouldShowNavigation}
        allowTouchMove={isMobile}
        //
        preventClicks={isMobile}
        preventClicksPropagation={isMobile}
        noSwiping={!isMobile}
        watchSlidesProgress
        //
        onSwiper={(swiper) => {
          sliderRef.current = swiper;
        }}
      >

        {/* Recipe Cards */}
        {
          recipes.map((recipe, index) => {
            const { id } = recipe;
            return (
              <Slide key={`recipe-slide-${index}-${id}`} className={styles.slide}>
                <RecipeCard
                  className={styles.card}
                  recipe={recipe}
                  onEnter={() => {
                    handleRecipeEnter(recipe);
                  }}
                  onClick={(e) => {
                    handleRecipeClick(recipe, e);
                  }}
                  onViewIngredientsClick={(e) => {
                    handleRecipeViewIngredientsClick(recipe, e);
                  }}
                />
              </Slide>
            );
          })
        }

        {/* View More Card */}

        {
          (link?.url && link?.text) && (
            <Slide
              className={classnames(
                styles.slide,
                styles.viewMoreSlide
              )}
            >
              <ViewMoreLink
                className={styles.viewMoreCard}
                link={link}
                name={title}
                asCard
              />
            </Slide>
          )
        }

      </Slider>
    </div>
  );
};

// Components

// TODO: merge with ProductsCarousel ViewMoreLink

interface ViewMoreLinkProps {
  className?: string,
  name?: string,
  link: {
    text: string,
    url: string
  },
  asCard?: boolean,
  onLinkClick?: () => void
}

const ViewMoreLink = (props: ViewMoreLinkProps) => {
  const {
    className,
    name,
    link,
    asCard,
    onLinkClick
  } = props;

  const {
    url, text
  } = link || {};

  // Hooks

  const analytics = useAnalytics();

  // Empty

  if (!url || !text) return null;

  // Render

  return (
    <Link
      href={url}
      prefetch={false}
      passHref
    >
      <Button
        tag="a"
        className={classnames(
          styles.viewMore,
          { [styles.viewMoreCard]: asCard },
          { [styles.viewMoreLink]: !asCard },
          className
        )}
        contentClassName={styles.viewMoreContent}
        onClick={() => {
          analytics.selectRecipeCarousel(name);
          if (onLinkClick) {
            onLinkClick();
          }
        }}
      >
        {
          !asCard
            ? (
              // Card
              <>
                <span className={styles.viewMoreText}>
                  {text}
                </span>
                <Icon
                  className={styles.viewMoreIcon}
                  name="arrow-narrow-right"
                  size={18}
                  strokeWidth={2}
                />
              </>
            ) : (
              // Button
              <>
                <div className={styles.viewMoreIcon}>
                  <Icon
                    className={styles.icon}
                    name="arrow-narrow-right"
                    size={24}
                    strokeWidth={2}
                  />
                </div>
                <div className={styles.viewMoreText}>{text}</div>
              </>
            )
        }
      </Button>
    </Link>
  );
};

// Constants

const DEFAULT_BREAKPOINTS = {
  [parseInt(styles.breakpointMd, 10)]: {
    slidesPerView: 4,
    slidesPerGroup: 4,
  },
  [parseInt(styles.breakpointSm, 10)]: {
    slidesPerView: 3,
    slidesPerGroup: 3,
  },
};

// Export

export default RecipeCarousel;
